import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import {
  Users, Plus, Search, Filter, MoreVertical, Edit, Trash2, 
  CheckCircle, XCircle, Clock, Shield, UserPlus, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmByTyping } from '../components/ConfirmByTyping';

import { API_BASE as API_URL } from '../lib/apiBase';
export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const { entities } = useOutletContext() || {};
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [roles, setRoles] = useState({ system_roles: [], custom_roles: [] });
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [dangerAction, setDangerAction] = useState(null);
  const [isPermissionDialogOpen, setIsPermissionDialogOpen] = useState(false);
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [usersRes, pendingRes, rolesRes, permsRes] = await Promise.all([
        fetch(`${API_URL}/api/users`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/users/pending-approval`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/roles`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/permissions`, { headers, credentials: 'include' })
      ]);

      if (usersRes.ok) setUsers(await usersRes.json());
      if (pendingRes.ok) setPendingUsers(await pendingRes.json());
      if (rolesRes.ok) setRoles(await rolesRes.json());
      if (permsRes.ok) setPermissions(await permsRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId, action) => {
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/users/${userId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ action }),
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success(`User ${action}d successfully`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to ${action} user`);
      }
    } catch (error) {
      toast.error(`Failed to ${action} user`);
    }
  };

  const handleUpdateUser = async (userId, updateData) => {
    // Danger zone: a role change or a deactivation must be confirmed by typing the name
    const target = users.find(u => u.user_id === userId);
    const roleChanged = target && updateData.role && updateData.role !== target.role;
    const beingDeactivated = target && target.is_active !== false && updateData.is_active === false;
    if (target && (roleChanged || beingDeactivated)) {
      setDangerAction({
        name: target.name || target.email,
        title: roleChanged ? 'Confirm role change' : 'Confirm deactivation',
        description: roleChanged
          ? `You are changing ${target.name}'s role from "${target.role}" to "${updateData.role}". This changes what they can see and do across the whole system.`
          : `${target.name} will lose access immediately and will not be able to log in.`,
        actionLabel: roleChanged ? 'Change role' : 'Deactivate',
        run: () => doUpdateUser(userId, updateData),
      });
      return;
    }
    doUpdateUser(userId, updateData);
  };

  const doUpdateUser = async (userId, updateData) => {
    setDangerAction(null);
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(updateData),
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('User updated successfully');
        setIsDialogOpen(false);
        setEditingUser(null);
        setIsPermissionDialogOpen(false);
        setSelectedUserForPermissions(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (user) => {
    setDangerAction({
      name: user.name || user.email,
      title: 'Deactivate user',
      description: `${user.name || user.email} will lose access immediately and will not be able to log in. Their records are kept.`,
      actionLabel: 'Deactivate',
      run: () => doDeleteUser(user.user_id),
    });
  };

  const doDeleteUser = async (userId) => {
    setDangerAction(null);
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('User deactivated');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to deactivate user');
      }
    } catch (error) {
      toast.error('Failed to deactivate user');
    }
  };

  const openPermissionDialog = (user) => {
    setSelectedUserForPermissions(user);
    setIsPermissionDialogOpen(true);
  };

  const allRoles = [...roles.system_roles, ...roles.custom_roles];

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && user.is_active) ||
      (statusFilter === 'inactive' && !user.is_active) ||
      (statusFilter === 'pending' && user.approval_status === 'pending');
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'bg-red-600 text-white',
      admin: 'bg-purple-600 text-white',
      hr: 'bg-blue-600 text-white',
      principal: 'bg-indigo-600 text-white',
      hod: 'bg-cyan-600 text-white',
      finance: 'bg-green-600 text-white',
      employee: 'bg-slate-500 text-white'
    };
    return colors[role] || 'bg-slate-500 text-white';
  };

  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isHR = currentUser?.role === 'hr' || isSuperAdmin;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
            User Management
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Manage users, approve registrations, and assign roles
          </p>
        </div>
      </div>

      {/* Pending Approvals Alert */}
      {pendingUsers.length > 0 && isHR && (
        <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-yellow-600" />
                <div>
                  <p className="font-medium text-yellow-800 dark:text-yellow-400">
                    {pendingUsers.length} users pending approval
                  </p>
                  <p className="text-sm text-yellow-600">
                    Review and approve new user registrations
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                Requires Action
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or email..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="user-search"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="role-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="hr">HR</SelectItem>
                <SelectItem value="principal">Principal</SelectItem>
                <SelectItem value="hod">HOD</SelectItem>
                <SelectItem value="finance">Finance</SelectItem>
                <SelectItem value="employee">Employee</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Tabs */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all-users">
            All Users ({users.length})
          </TabsTrigger>
          {isHR && (
            <TabsTrigger value="pending" data-testid="tab-pending-users">
              Pending Approval ({pendingUsers.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="all">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#DC2626]" />
                Users ({filteredUsers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-16 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
                  ))}
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-medium">No users found</h3>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Entity</TableHead>
                        <TableHead>Permissions</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map(user => (
                        <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={user.picture} />
                                <AvatarFallback className="bg-[#DC2626] text-white">
                                  {getInitials(user.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{user.name}</p>
                                <p className="text-sm text-slate-500">{user.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getRoleBadgeColor(user.role)}>
                              {user.role?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {entities?.find(e => e.entity_id === user.entity_id)?.name || 
                              (user.role === 'super_admin' ? 'All Entities' : '-')}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="cursor-pointer" onClick={() => openPermissionDialog(user)}>
                              {user.permissions?.length || 0} permissions
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <Badge className={user.is_active ? 'badge-success' : 'badge-error'}>
                                {user.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                              {user.approval_status === 'pending' && (
                                <Badge className="badge-warning">Pending Approval</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {user.role !== 'super_admin' && (isSuperAdmin || (isHR && user.role !== 'admin')) && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" data-testid={`user-actions-${user.user_id}`}>
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    setEditingUser(user);
                                    setIsDialogOpen(true);
                                  }}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Edit User
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openPermissionDialog(user)}>
                                    <Shield className="w-4 h-4 mr-2" />
                                    Manage Permissions
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    className="text-red-600"
                                    onClick={() => handleDeleteUser(user)}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Deactivate
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {isHR && (
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-600" />
                  Pending Approvals
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingUsers.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 mx-auto text-green-300 mb-4" />
                    <h3 className="text-lg font-medium">No pending approvals</h3>
                    <p className="text-slate-500">All user registrations have been processed</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingUsers.map(user => (
                      <div 
                        key={user.user_id}
                        className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg"
                        data-testid={`pending-user-${user.user_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={user.picture} />
                            <AvatarFallback className="bg-yellow-500 text-white">
                              {getInitials(user.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-sm text-slate-500">{user.email}</p>
                            <p className="text-xs text-slate-400">
                              Registered: {new Date(user.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleApproveUser(user.user_id, 'approve')}
                            data-testid={`approve-user-${user.user_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleApproveUser(user.user_id, 'reject')}
                            data-testid={`reject-user-${user.user_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Danger-zone confirmation (type the name) */}
      <ConfirmByTyping
        open={!!dangerAction}
        onOpenChange={(o) => { if (!o) setDangerAction(null); }}
        title={dangerAction?.title || ''}
        description={dangerAction?.description || ''}
        confirmWord={dangerAction?.name || ''}
        actionLabel={dangerAction?.actionLabel || 'Confirm'}
        onConfirm={() => dangerAction?.run()}
      />

      {/* Edit User Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) setEditingUser(null);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user details and role</DialogDescription>
          </DialogHeader>
          {editingUser && (
            <EditUserForm 
              user={editingUser} 
              roles={allRoles}
              entities={entities}
              users={users}
              onSave={handleUpdateUser}
              onCancel={() => setIsDialogOpen(false)}
              isSuperAdmin={isSuperAdmin}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Permission Management Dialog */}
      <Dialog open={isPermissionDialogOpen} onOpenChange={(open) => {
        setIsPermissionDialogOpen(open);
        if (!open) setSelectedUserForPermissions(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Permissions</DialogTitle>
            <DialogDescription>
              Customize permissions for {selectedUserForPermissions?.name}
            </DialogDescription>
          </DialogHeader>
          {selectedUserForPermissions && (
            <PermissionManager 
              user={selectedUserForPermissions}
              permissions={permissions}
              onSave={handleUpdateUser}
              onCancel={() => setIsPermissionDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditUserForm({ user, roles, entities, users, onSave, onCancel, isSuperAdmin }) {
  const [formData, setFormData] = useState({
    name: user.name || '',
    role: user.role || 'employee',
    entity_id: user.entity_id || '',
    extra_entity_ids: user.extra_entity_ids || [],
    department: user.department || '',
    reports_to: user.reports_to || '',
    is_active: user.is_active !== false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(user.user_id, formData);
  };

  const systemRoles = ['super_admin', 'admin', 'hr', 'principal', 'hod', 'finance', 'employee'];

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Name</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>Role</Label>
        <Select
          value={formData.role}
          onValueChange={(value) => setFormData({ ...formData, role: value })}
          disabled={!isSuperAdmin && ['super_admin', 'admin'].includes(formData.role)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {systemRoles.filter(r => isSuperAdmin || !['super_admin', 'admin'].includes(r)).map(role => (
              <SelectItem key={role} value={role}>
                {role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Entity</Label>
        <Select
          value={formData.entity_id || '__none__'}
          onValueChange={(value) => setFormData({ ...formData, entity_id: value === '__none__' ? '' : value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select entity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None (All Entities)</SelectItem>
            {entities?.map(entity => (
              <SelectItem key={entity.entity_id} value={entity.entity_id}>
                {entity.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Additional Institutes (multi-institute access)</Label>
        <p className="text-xs text-slate-500">
          Tick extra institutes this user should also be able to see and switch to — e.g. one HR
          handling both Sanjay Ghodawat Institute and Sanjay Ghodawat Industrial Institute.
        </p>
        <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 p-3 space-y-2">
          {(entities || []).filter(e => e.entity_id !== formData.entity_id).map(entity => (
            <div key={entity.entity_id} className="flex items-center gap-2">
              <Checkbox
                id={`extra-ent-${entity.entity_id}`}
                data-testid={`extra-entity-${entity.entity_id}`}
                checked={formData.extra_entity_ids.includes(entity.entity_id)}
                onCheckedChange={(checked) => setFormData({
                  ...formData,
                  extra_entity_ids: checked
                    ? [...formData.extra_entity_ids, entity.entity_id]
                    : formData.extra_entity_ids.filter(id => id !== entity.entity_id)
                })}
              />
              <Label htmlFor={`extra-ent-${entity.entity_id}`} className="text-sm font-normal cursor-pointer">
                {entity.name}
              </Label>
            </div>
          ))}
          {(entities || []).length <= 1 && <p className="text-xs text-slate-400">No other institutes available</p>}
        </div>
      </div>
      <div className="space-y-2">
        <Label>Reports To (for leave approval)</Label>
        <Select
          value={formData.reports_to || '__none__'}
          onValueChange={(value) => setFormData({ ...formData, reports_to: value === '__none__' ? '' : value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select supervisor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None</SelectItem>
            {users?.filter(u => u.user_id !== user.user_id && ['hod', 'principal', 'hr', 'admin'].includes(u.role)).map(u => (
              <SelectItem key={u.user_id} value={u.user_id}>
                {u.name} ({u.role})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center space-x-2">
        <Checkbox
          id="is_active"
          checked={formData.is_active}
          onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
        />
        <Label htmlFor="is_active">Active</Label>
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="btn-primary">Save Changes</Button>
      </div>
    </form>
  );
}

function PermissionManager({ user, permissions, onSave, onCancel }) {
  const [customPermissions, setCustomPermissions] = useState(user.custom_permissions || []);
  const [revokedPermissions, setRevokedPermissions] = useState(user.revoked_permissions || []);

  const groupedPermissions = permissions.reduce((acc, perm) => {
    const category = perm.category_code;
    if (!acc[category]) acc[category] = [];
    acc[category].push(perm);
    return acc;
  }, {});

  const categoryNames = {
    users: 'User Management',
    roles: 'Role Management',
    entities: 'Entity Management',
    employees: 'Employee Management',
    attendance: 'Attendance',
    leaves: 'Leave Management',
    payroll: 'Payroll',
    reports: 'Reports',
    settings: 'Settings'
  };

  const toggleCustomPermission = (permCode) => {
    if (customPermissions.includes(permCode)) {
      setCustomPermissions(customPermissions.filter(p => p !== permCode));
    } else {
      setCustomPermissions([...customPermissions, permCode]);
      // Remove from revoked if adding
      setRevokedPermissions(revokedPermissions.filter(p => p !== permCode));
    }
  };

  const toggleRevokedPermission = (permCode) => {
    if (revokedPermissions.includes(permCode)) {
      setRevokedPermissions(revokedPermissions.filter(p => p !== permCode));
    } else {
      setRevokedPermissions([...revokedPermissions, permCode]);
      // Remove from custom if revoking
      setCustomPermissions(customPermissions.filter(p => p !== permCode));
    }
  };

  const handleSave = () => {
    onSave(user.user_id, {
      custom_permissions: customPermissions,
      revoked_permissions: revokedPermissions
    });
  };

  const basePermissions = user.permissions || [];

  return (
    <div className="space-y-4">
      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          <strong>Base Role:</strong> {user.role?.replace('_', ' ')} with {basePermissions.length} default permissions
        </p>
        <p className="text-sm text-slate-500 mt-1">
          Add custom permissions or revoke existing ones below.
        </p>
      </div>

      <Accordion type="multiple" className="w-full">
        {Object.entries(groupedPermissions).map(([category, perms]) => (
          <AccordionItem key={category} value={category}>
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>{categoryNames[category] || category}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2 p-4">
                {perms.map(perm => {
                  const isBasePermission = basePermissions.includes(perm.code);
                  const isCustomAdded = customPermissions.includes(perm.code);
                  const isRevoked = revokedPermissions.includes(perm.code);
                  
                  return (
                    <div key={perm.code} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{perm.name}</span>
                        {isBasePermission && !isRevoked && (
                          <Badge variant="outline" className="text-xs">From Role</Badge>
                        )}
                        {isCustomAdded && (
                          <Badge className="badge-success text-xs">Added</Badge>
                        )}
                        {isRevoked && (
                          <Badge className="badge-error text-xs">Revoked</Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {!isBasePermission && (
                          <Button
                            size="sm"
                            variant={isCustomAdded ? 'default' : 'outline'}
                            onClick={() => toggleCustomPermission(perm.code)}
                          >
                            {isCustomAdded ? 'Remove' : 'Add'}
                          </Button>
                        )}
                        {isBasePermission && (
                          <Button
                            size="sm"
                            variant={isRevoked ? 'destructive' : 'outline'}
                            onClick={() => toggleRevokedPermission(perm.code)}
                          >
                            {isRevoked ? 'Restore' : 'Revoke'}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="flex justify-between items-center pt-4 border-t">
        <div className="text-sm text-slate-500">
          <span className="text-green-600">+{customPermissions.length} added</span>
          {' • '}
          <span className="text-red-600">-{revokedPermissions.length} revoked</span>
        </div>
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button className="btn-primary" onClick={handleSave}>Save Permissions</Button>
        </div>
      </div>
    </div>
  );
}
